import React from "react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n/i18n";
import { SectionHeading } from "@/components/shared";
import { ArrowRight, Sprout, Building2, Factory, Landmark, TrendingUp, ShieldCheck } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1751210769268-85d43ecfcdd8?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

function FlowDiagram() {
  const { t } = useT();
  const steps = t("flow");
  return (
    <div className="flex flex-wrap items-center gap-2">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <span className="rounded-md border border-forest/20 bg-forest/5 px-3 py-2 text-sm font-medium text-forest">{s}</span>
          {i < steps.length - 1 && <ArrowRight className="h-4 w-4 text-soil" aria-hidden="true" />}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function Home() {
  const { t } = useT();
  const priorities = ["children", "hospitals", "seniors", "military", "workers"];
  const ctas = [
    { to: "/register", key: "registerCrop", icon: Sprout },
    { to: "/register", key: "registerDemand", icon: Building2 },
    { to: "/contact", key: "becomePartner", icon: Factory },
    { to: "/how-it-works", key: "viewModel", icon: ArrowRight },
  ];
  const serve = [
    { icon: Sprout, title: t("roles.farmer"), to: "/farmer-protection" },
    { icon: Building2, title: t("nav.institutional"), to: "/institutional-food" },
    { icon: Factory, title: t("roles.processor"), to: "/circular-economy" },
    { icon: Landmark, title: t("nav.governance"), to: "/governance" },
    { icon: TrendingUp, title: t("nav.investors"), to: "/investors" },
    { icon: ShieldCheck, title: t("nav.zeroRot"), to: "/zero-rot" },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative isolate overflow-hidden bg-forestdark">
        <img src={HERO} alt="Freshly harvested Polish grain" className="absolute inset-0 h-full w-full object-cover" />
        <div className="zp-hero-overlay absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:py-32">
          <div className="max-w-2xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white">
              {t("slogan")}
            </div>
            <h1 className="font-heading text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl">
              {t("home.heroTitle")}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/85">{t("home.heroSub")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-register-crop" className="inline-flex items-center gap-2 rounded-md bg-white px-5 py-3 text-sm font-semibold text-forest hover:bg-white/90">
                {t("cta.registerCrop")} <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link to="/how-it-works" data-testid="hero-view-model" className="inline-flex items-center gap-2 rounded-md border border-white/40 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10">
                {t("cta.seeHow")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Zero-Rot band */}
      <section className="border-b border-subtle bg-white">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-14 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-2 text-sm font-semibold uppercase tracking-wide text-polishred">{t("home.zeroRotTitle")}</div>
            <p className="font-heading text-xl leading-relaxed text-charcoal sm:text-2xl">"{t("home.zeroRot")}"</p>
          </div>
          <div className="rounded-md border border-forest/15 bg-forest/[0.04] p-5">
            <div className="text-sm font-semibold text-forest">Doktryna</div>
            <p className="mt-2 text-sm leading-relaxed text-charcoal">{t("home.doctrine")}</p>
            <Link to="/konrad-kwiatkowski" data-testid="home-founder-link" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-forest hover:underline">
              {t("nav.founder")} <ArrowRight className="h-3 w-3" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Priority populations */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <SectionHeading eyebrow={t("home.priorityTitle")} title={t("home.priorityTitle")} />
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {priorities.map((p, i) => (
            <div key={p} className="rounded-md border border-subtle bg-white p-5 text-center">
              <div className="mx-auto mb-2 grid h-10 w-10 place-items-center rounded-full bg-forest/10 font-heading font-bold text-forest">{i + 1}</div>
              <div className="text-sm font-semibold text-charcoal">{t(`priorities.${p}`)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Flow */}
      <section className="bg-secondary/40">
        <div className="mx-auto max-w-7xl px-4 py-16">
          <SectionHeading eyebrow="Model" title={t("home.flowTitle")} />
          <div className="mt-8"><FlowDiagram /></div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[["Fresh food", "Świeża żywność"], ["Processing-grade", "Klasa przetwórcza"], ["Organic residue", "Pozostałości organiczne"], ["Financial settlement", "Rozliczenie finansowe"]].map(([en, pl]) => (
              <div key={en} className="rounded-md border border-subtle bg-white p-4 text-sm text-charcoal">{t("langName") === "Polski" ? pl : en}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Metrics */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <SectionHeading eyebrow={t("home.metricsTitle")} title={t("home.metricsTitle")}>
          {t("demo")}
        </SectionHeading>
        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[["42 000 kg", "organized (demo)"], ["3", "institutions (demo)"], ["Lubelskie / Mazowieckie", "pilot region"], ["Truskawka 2027", "first live pilot"]].map(([v, l]) => (
            <div key={l} className="rounded-md border border-subtle bg-white p-5">
              <div className="font-heading text-2xl font-bold text-forest">{v}</div>
              <div className="mt-1 text-sm text-mutedtext">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA grid */}
      <section className="bg-forest">
        <div className="mx-auto max-w-7xl px-4 py-14">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {ctas.map(({ to, key, icon: Icon }) => (
              <Link key={key} to={to} className="group rounded-md border border-white/15 bg-white/5 p-5 text-white transition-colors hover:bg-white/10">
                <Icon className="mb-3 h-6 w-6" aria-hidden="true" />
                <div className="flex items-center justify-between text-sm font-semibold">{t(`cta.${key}`)} <ArrowRight className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Who we serve teaser */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <SectionHeading eyebrow={t("nav.serve")} title={t("nav.serve")} />
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {serve.map(({ icon: Icon, title, to }) => (
            <Link key={title} to={to} className="flex items-center gap-4 rounded-md border border-subtle bg-white p-5 transition-colors hover:border-forest/40">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-forest/10"><Icon className="h-5 w-5 text-forest" aria-hidden="true" /></span>
              <span className="text-sm font-semibold text-charcoal">{title}</span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
